import { useState } from 'react';
import ButtonNB from '../ui-nb/ButtonNB';
import IconNB from '../ui-nb/IconNB';
import { useStatusTransition } from '../../hooks/useStatusTransition';

interface StatusOption {
  id: string;
  name: string;
  position: number;
}

interface StatusDropdownProps {
  orderId: string;
  currentStatusId: string;
  availableStatuses: StatusOption[];
  changedBy: string;
  onSuccess?: () => void;
  disabled?: boolean;
  blockedStatuses?: { statusName: string; reason: string }[];
}

export default function StatusDropdown({
  orderId,
  currentStatusId,
  availableStatuses,
  changedBy,
  onSuccess,
  disabled = false,
  blockedStatuses = [],
}: StatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipId, setTooltipId] = useState<string | null>(null);
  const { transitionStatus, isLoading, error: hookError } = useStatusTransition();

  const currentStatus = availableStatuses.find(s => s.id === currentStatusId);
  const nextStatuses = availableStatuses.filter(s => s.position > (currentStatus?.position || 0));
  const blockedNames = new Set(blockedStatuses.map(b => b.statusName));

  const getBlockReason = (statusName: string): string | null => {
    const blocked = blockedStatuses.find(b => b.statusName === statusName);
    return blocked ? blocked.reason : null;
  };

  const handleSelect = async (statusId: string) => {
    setIsOpen(false);

    const success = await transitionStatus(orderId, statusId, changedBy);
    if (success) {
      onSuccess?.();
    }
  };

  if (disabled) {
    return (
      <div className="px-4 py-2 bg-nb-cream border-2 border-black text-black font-medium">
        {currentStatus?.name || 'Unknown'}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading || disabled}
        className="w-full md:w-auto px-4 py-2 font-medium flex items-center justify-between gap-4 bg-nb-yellow border-2 border-black text-black hover:bg-nb-yellow/90 disabled:opacity-50"
      >
        <span>{currentStatus?.name || 'Select status'}</span>
        <span className="text-sm">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-2 w-72 bg-white border-2 border-black shadow-lg">
          {nextStatuses.length === 0 && blockedStatuses.length === 0 ? (
            <div className="px-4 py-2 text-sm text-nb-text-muted">
              No further status transitions available
            </div>
          ) : (
            nextStatuses.map(status => {
              const blockReason = getBlockReason(status.name);
              const isBlocked = blockReason !== null;

              return (
                <div key={status.id} className="relative">
                  <button
                    type="button"
                    onClick={() => !isBlocked && handleSelect(status.id)}
                    disabled={isBlocked}
                    className={`w-full px-4 py-2 text-left flex items-center justify-between
                      ${isBlocked
                        ? 'bg-nb-cream text-nb-text-muted cursor-not-allowed'
                        : 'bg-white hover:bg-nb-yellow text-black'
                      }
                      first:rounded-t last:rounded-b`}
                    onMouseEnter={() => isBlocked && setTooltipId(status.id)}
                    onMouseLeave={() => setTooltipId(null)}
                  >
                    <span>{status.name}</span>
                    {isBlocked && (
                      <IconNB name="lock" size={14} className="text-nb-text-muted flex-shrink-0" />
                    )}
                  </button>
                  {isBlocked && tooltipId === status.id && (
                    <div className="absolute left-0 right-0 -top-10 mx-2 px-3 py-1.5 bg-nb-black text-white text-xs rounded shadow-lg z-20 text-center">
                      {blockReason}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {hookError && (
        <p className="text-xs text-nb-red mt-1">{hookError}</p>
      )}

      {isLoading && (
        <p className="text-xs text-nb-text-muted mt-1">Updating status...</p>
      )}
    </div>
  );
}